import type { BlockedArea, UnmetClause } from '@api/assistant/assistantApi';
import { Clause, RegisteredArea, SecuredFlag } from './types';

/**
 * Which mounted secured areas are hidden or read-only for the current user, and the clauses that are
 * not satisfied — for the assistant's page context, so it can say which permission or state is missing.
 *
 * Reads only areas (they unregister on unmount), never `getFlags()`: flags are never unregistered, so
 * they include pages the user has already left.
 */

const MAX_AREAS = 20;

/**
 * `flag` is the rule the clause belongs to; none for a clause declared inline on the area. A clause's own
 * description / byDesign wins over its rule's, so one rule can mix e.g. an owner-only condition with a lock
 * that a permission lifts.
 */
const toUnmet = (clause: Clause, flag?: SecuredFlag): UnmetClause => {
  let anyOf: string[] | null = null;
  if (clause.type === 'permission' || clause.type === 'feature') anyOf = clause.anyOf.map(String);
  if (clause.type === 'module') anyOf = clause.anyOf.map(m => String(m.module));
  return {
    type: clause.type,
    label: clause.label ?? null,
    any_of: anyOf,
    rule: flag?.id ?? null,
    description: clause.description ?? flag?.metadata?.description ?? null,
    by_design: clause.byDesign ?? flag?.metadata?.byDesign ?? null,
  };
};

const unmetOf = (inline: Clause[], flags: SecuredFlag[]): UnmetClause[] => {
  const unmet = inline.filter(c => !c.satisfied).map(c => toUnmet(c));
  flags
    .filter(flag => !flag.granted)
    .forEach(flag => {
      const clauses = (flag.metadata?.clauses ?? []).filter(c => !c.satisfied);
      // No metadata (or a hand-written rule whose clauses all pass): the flag itself is all we can name.
      if (clauses.length === 0) unmet.push(toUnmet({ type: 'custom', label: flag.id, satisfied: false }, flag));
      else unmet.push(...clauses.map(c => toUnmet(c, flag)));
    });
  // The same condition reached twice (e.g. an inline clause repeated) is sent once.
  const seen = new Set<string>();
  return unmet.filter(clause => {
    const key = JSON.stringify(clause);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export interface CollectedBlockedAreas {
  /** Page content, the free-form conditions first. */
  blocked: BlockedArea[];
  /** Menu entries (`menu-…`): hidden on every page, so kept apart from what the user is looking at. */
  navigation: BlockedArea[];
}

const MAX_NAVIGATION = 15;
const MAX_INSTANCES = 10;
/** Conditions only the frontend can explain; the assistant explains permissions, features and modules itself. */
const FREE_FORM: ReadonlySet<UnmetClause['type']> = new Set(['ownership', 'state', 'role', 'custom']);

export const collectBlockedAreas = (areas: RegisteredArea[]): CollectedBlockedAreas => {
  const groups = new Map<string, BlockedArea>();
  for (const area of areas) {
    if (area.visible && area.editable !== false) continue;
    const blocked = area.visible ? 'editable' : 'visible';
    const unmet = blocked === 'visible' ? unmetOf(area.showInline, area.showFlags) : unmetOf(area.editableInline, area.editableFlags);
    // Every clause must hold for the area to show, so a failing `data` clause means there is nothing to show,
    // whatever else fails: not a block.
    if (unmet.some(c => c.type === 'data')) continue;

    const label = area.label ?? null;
    const description = area.description ?? null;
    const note = area.note ?? null;
    // The rows of one repeated area that are blocked for the same reason become one entry listing the
    // instances; rows blocked for another reason become another entry with the same id.
    const key = JSON.stringify([area.baseId, blocked, label, description, note, unmet]);
    let group = groups.get(key);
    if (!group) {
      group = { id: area.baseId, instances: null, count: 0, label, description, note, blocked, unmet };
      groups.set(key, group);
    }
    group.count++;
    if (area.instance !== undefined) {
      const instances = (group.instances ??= []);
      if (instances.length < MAX_INSTANCES && !instances.includes(area.instance)) instances.push(area.instance);
    }
  }

  const entries = Array.from(groups.values());
  const isNavigation = (entry: BlockedArea) => entry.id.startsWith('menu-');
  const freeForm = (entry: BlockedArea) => entry.unmet.some(c => FREE_FORM.has(c.type));
  const content = entries.filter(entry => !isNavigation(entry));
  return {
    // Registration order within each group (the sort is stable).
    blocked: [...content].sort((a, b) => Number(freeForm(b)) - Number(freeForm(a))).slice(0, MAX_AREAS),
    navigation: entries.filter(isNavigation).slice(0, MAX_NAVIGATION),
  };
};
